import React, { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "../Includes/Sidebar";
import Footer from "../Includes/Footer";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import GeneralService from "../../services/general.service";

export default function ViewStaff() {
    const [staff, setStaff] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const state = useSelector((state) => state.stateVals);
    const { id: adminId } = state;
    const BASE_IMAGE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.azmrehabcentre.pk';

    // Fetch staff data
    const fetchStaff = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await GeneralService.getAllStaff();
            console.log("API Response:", response.data);
            
            let staffData = [];
            if (response.data && Array.isArray(response.data)) {
                staffData = response.data;
            } else if (response.data && response.data.staff && Array.isArray(response.data.staff)) {
                staffData = response.data.staff;
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                staffData = response.data.data;
            }
            
            // Ensure each staff member has required properties
            const sanitizedStaff = staffData.map(member => ({
                id: member.id || Math.random().toString(36).substr(2, 9),
                name: member.name || '',
                position: member.position || '',
                gender: member.gender || '',
                phone: member.phone || '',
                email: member.email || '',
                address: member.address || '',
                salary: member.salary || 0,
                image: member.image || '',
                created_at: member.created_at || new Date().toISOString(),
                ...member
            }));
            
            setStaff(sanitizedStaff);
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || "Failed to fetch staff";
            setError(errorMessage);
            console.error("Fetch staff error:", err);
            setStaff([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    // Enhanced delete function with better error handling
    const removeStaff = async (id, staffName) => {
        const result = await Swal.fire({
            title: "Delete Staff Member",
            html: `Are you sure you want to permanently delete <strong>${staffName}</strong>?<br><small class="text-muted">This action cannot be undone.</small>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Delete!",
            cancelButtonText: "Cancel",
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                confirmButton: 'btn btn-danger',
                cancelButton: 'btn btn-secondary'
            }
        });

        if (!result.isConfirmed) return;

        try {
            // Show loading indicator
            Swal.fire({
                title: 'Deleting...',
                text: 'Please wait while we delete the staff member.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await GeneralService.deleteStaffById(id);
            console.log("Delete response:", response);
            
            // Check various response patterns for success
            const isSuccess = response && (
                response.success === true || 
                response.status === 'success' || 
                response.status === 200 ||
                response.statusText === 'OK' ||
                (response.data && (response.data.success === true || response.data.status === 'success'))
            );
            
            if (isSuccess) {
                // Update local state
                setStaff(prevStaff => prevStaff.filter(staffMember => staffMember.id !== id));
                
                // Reset pagination if needed
                const updatedStaffCount = staff.filter(s => s.id !== id).length;
                const newTotalPages = Math.ceil(updatedStaffCount / recordsPerPage);
                if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(1);
                }
                
                Swal.fire({
                    title: "Deleted!",
                    text: `${staffName} has been removed successfully.`,
                    icon: "success",
                    timer: 3000,
                    showConfirmButton: false,
                    timerProgressBar: true
                });
            } else {
                throw new Error(response?.message || response?.data?.message || 'Delete operation failed');
            }
        } catch (err) {
            console.error("Delete staff error:", err);
            
            // Fallback: refresh data to check if deletion actually worked
            try {
                await fetchStaff();
                const staffStillExists = staff.some(s => s.id === id);
                if (!staffStillExists) {
                    Swal.fire({
                        title: "Deleted!",
                        text: `${staffName} has been removed successfully.`,
                        icon: "success",
                        timer: 3000,
                        showConfirmButton: false
                    });
                    return;
                }
            } catch (refreshError) {
                console.error("Failed to refresh after delete error:", refreshError);
            }
            
            Swal.fire({
                title: "Error!",
                text: err.response?.data?.message || err.message || "Failed to delete staff member. Please try again.",
                icon: "error",
                confirmButtonText: "OK"
            });
        }
    };

    // Debounced search with better performance
    const debouncedSearch = useMemo(
        () => debounce((query) => {
            setSearchQuery(query);
            setCurrentPage(1);
        }, 300),
        []
    );

    const handleSearch = (e) => {
        debouncedSearch(e.target.value);
    };

    // Sorting functionality
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Enhanced filtering and sorting
    const processedStaff = useMemo(() => {
        let result = Array.isArray(staff) ? [...staff] : [];
        
        // Filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter((staffMember) => {
                if (!staffMember) return false;
                
                const searchableFields = [
                    staffMember.name,
                    staffMember.position,
                    staffMember.email,
                    staffMember.address,
                    staffMember.phone?.toString(),
                    staffMember.salary?.toString(),
                    staffMember.gender
                ];
                
                return searchableFields.some(field => 
                    field && field.toString().toLowerCase().includes(query)
                );
            });
        }
        
        // Sort
        if (sortConfig.key) {
            result.sort((a, b) => {
                let aValue = a[sortConfig.key] || '';
                let bValue = b[sortConfig.key] || '';
                
                // Handle numeric values
                if (sortConfig.key === 'salary') {
                    aValue = Number(aValue) || 0;
                    bValue = Number(bValue) || 0;
                } else {
                    aValue = aValue.toString().toLowerCase();
                    bValue = bValue.toString().toLowerCase();
                }
                
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        return result;
    }, [staff, searchQuery, sortConfig]);

    // Pagination calculations
    const totalRecords = processedStaff.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const displayedStaff = processedStaff.slice(startIndex, startIndex + recordsPerPage);

    // Image error handling
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        const placeholder = e.target.nextElementSibling;
        if (placeholder) {
            placeholder.style.display = 'flex';
        }
    };

    // Refresh handler
    const handleRefresh = () => {
        setSearchQuery("");
        setCurrentPage(1);
        setSortConfig({ key: null, direction: 'asc' });
        fetchStaff();
    };

    // Export functionality
    const handleExport = () => {
        const csvContent = [
            ['Name', 'Position', 'Gender', 'Phone', 'Email', 'Address', 'Salary'],
            ...processedStaff.map(staff => [
                staff.name,
                staff.position,
                staff.gender,
                staff.phone,
                staff.email,
                staff.address,
                staff.salary
            ])
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `staff_list_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Sort indicator component
    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) {
            return <span className="sort-indicator">⇅</span>;
        }
        return (
            <span className="sort-indicator active">
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    return (
        <>
            <div className="content">
                <Sidebar />
                <div className="container-fluid pt-4 px-4">
                    <div className="bg-light text-center rounded p-4">
                        {/* Header */}
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="mb-0">
                                Staff Management 
                                <span className="number-records ms-2">({totalRecords} total)</span>
                            </h5>
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={handleExport}
                                    disabled={loading || totalRecords === 0}
                                    title="Export to CSV"
                                >
                                    📊 Export
                                </button>
                                <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={handleRefresh}
                                    disabled={loading}
                                    title="Refresh data"
                                >
                                    {loading ? '🔄 Loading...' : '🔄 Refresh'}
                                </button>
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="form-label small">Records per page:</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={recordsPerPage}
                                    onChange={(e) => {
                                        setRecordsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={5}>5 per page</option>
                                    <option value={10}>10 per page</option>
                                    <option value={25}>25 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            </div>
                            <div className="col-md-8">
                                <label className="form-label small">Search staff:</label>
                                <div className="input-group">
                                    <span className="input-group-text">🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Search by name, position, email, phone..."
                                        className="form-control"
                                        onChange={handleSearch}
                                    />
                                    {searchQuery && (
                                        <button 
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setSearchQuery("");
                                                setCurrentPage(1);
                                            }}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="bg-gred">
                                    <tr className="text-white">
                                        <th>#</th>
                                        <th>Photo</th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('name')}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            Staff Name <SortIndicator column="name" />
                                        </th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('position')}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            Position <SortIndicator column="position" />
                                        </th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('gender')}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            Gender <SortIndicator column="gender" />
                                        </th>
                                        <th>Phone</th>
                                        <th>Email</th>
                                        <th>Address</th>
                                        <th 
                                            className="sortable-header" 
                                            onClick={() => handleSort('salary')}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            Salary <SortIndicator column="salary" />
                                        </th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <div className="mt-2">Loading staff data...</div>
                                            </td>
                                        </tr>
                                    ) : error ? (
                                        <tr>
                                            <td colSpan="10" className="text-center py-4">
                                                <div className="alert alert-danger mb-0">
                                                    <h6>⚠️ Error Loading Data</h6>
                                                    <p className="mb-2">{error}</p>
                                                    <button 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={handleRefresh}
                                                    >
                                                        Try Again
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : displayedStaff.length > 0 ? (
                                        displayedStaff.map((staffMember, index) => (
                                            <tr key={`staff-${staffMember.id}`} className="align-middle">
                                                <td className="fw-bold">{startIndex + index + 1}</td>
                                                <td>
                                                    <div className="position-relative" style={{ width: "80px", height: "80px" }}>
                                                        <img
                                                            src={staffMember.image && staffMember.image.startsWith('http')
                                                                ? staffMember.image
                                                                : `${BASE_IMAGE_URL}/${(staffMember.image || '').replace(/^\/+/, '')}`}
                                                            alt={`${staffMember.name}'s photo`}
                                                            className="staff-image"
                                                            style={{ 
                                                                width: '80px', 
                                                                height: '80px', 
                                                                objectFit: 'cover', 
                                                                borderRadius: '8px',
                                                                border: '2px solid #f44c00'
                                                            }}
                                                            onError={handleImageError}
                                                            loading="lazy"
                                                        />
                                                        <div 
                                                            className="image-placeholder"
                                                            style={{ 
                                                                width: '80px', 
                                                                height: '80px', 
                                                                backgroundColor: '#f8f9fa', 
                                                                border: '2px solid #dee2e6', 
                                                                borderRadius: '8px',
                                                                display: 'none', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center', 
                                                                fontSize: '11px', 
                                                                color: '#6c757d',
                                                                textAlign: 'center',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0
                                                            }}
                                                        >
                                                            👤<br />No Photo
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="fw-semibold">{staffMember.name || "N/A"}</td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {staffMember.position || "N/A"}
                                                    </span>
                                                </td>
                                                <td style={{ textTransform: 'capitalize' }}>
                                                    {staffMember.gender || "N/A"}
                                                </td>
                                                <td>
                                                    {staffMember.phone ? (
                                                        <a href={`tel:${staffMember.phone}`} className="text-decoration-none">
                                                            {staffMember.phone}
                                                        </a>
                                                    ) : "N/A"}
                                                </td>
                                                <td>
                                                    {staffMember.email ? (
                                                        <a href={`mailto:${staffMember.email}`} className="text-decoration-none">
                                                            {staffMember.email}
                                                        </a>
                                                    ) : "N/A"}
                                                </td>
                                                <td>
                                                    <span title={staffMember.address} className="text-truncate d-inline-block" style={{maxWidth: '150px'}}>
                                                        {staffMember.address || "N/A"}
                                                    </span>
                                                </td>
                                                <td className="text-success fw-bold">
                                                    {staffMember.salary ? `RS ${Number(staffMember.salary).toLocaleString()}` : "N/A"}
                                                </td>
                                                <td>
                                                    <div className="btn-group-vertical gap-1">
                                                        <Link 
                                                            className="btn btn-sm btn-primary" 
                                                            to={`/edit-staff/${staffMember.id}`}
                                                            title="Edit staff member"
                                                        >
                                                            ✏️ Edit
                                                        </Link>
                                                        <button 
                                                            className="btn btn-sm btn-danger" 
                                                            onClick={() => removeStaff(staffMember.id, staffMember.name)}
                                                            title="Delete staff member"
                                                        >
                                                            🗑️ Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="10" className="text-center py-5">
                                                <div className="text-muted">
                                                    <h5>📋 No Staff Found</h5>
                                                    <p>
                                                        {searchQuery 
                                                            ? `No staff members match "${searchQuery}"` 
                                                            : 'No staff members have been added yet'
                                                        }
                                                    </p>
                                                    {searchQuery && (
                                                        <button 
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={() => {
                                                                setSearchQuery("");
                                                                setCurrentPage(1);
                                                            }}
                                                        >
                                                            Clear Search
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Enhanced Pagination */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="text-muted small">
                                    Showing {startIndex + 1} to {Math.min(startIndex + recordsPerPage, totalRecords)} of {totalRecords} entries
                                </div>
                                <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                            >
                                                First
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </button>
                                        </li>
                                        
                                        {/* Page numbers with smart truncation */}
                                        {(() => {
                                            const pages = [];
                                            const maxVisiblePages = 5;
                                            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                                            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                                            
                                            if (endPage - startPage + 1 < maxVisiblePages) {
                                                startPage = Math.max(1, endPage - maxVisiblePages + 1);
                                            }
                                            
                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(
                                                    <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
                                                        <button 
                                                            className="page-link" 
                                                            onClick={() => setCurrentPage(i)}
                                                        >
                                                            {i}
                                                        </button>
                                                    </li>
                                                );
                                            }
                                            return pages;
                                        })()}
                                        
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button 
                                                className="page-link" 
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Last
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
                <Footer />
            </div>

            {/* Enhanced CSS */}
            <style jsx>{`
                .content {
                    min-height: 100vh;
                    background-color: #f8f9fa;
                }
                
                .table-responsive {
                    border-radius: 0.5rem;
                    box-shadow: 0 0 0 1px rgba(0,0,0,0.05);
                }
                
                .table {
                    margin-bottom: 0;
                    background-color: white;
                }
                
                .table th {
                    font-weight: 600;
                    font-size: 13px;
                    border-bottom: 2px solid #dee2e6;
                    position: relative;
                    user-select: none;
                }
                
                .sortable-header {
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                }
                
                .sortable-header:hover {
                    background-color: rgba(255,255,255,0.1) !important;
                }
                
                .sort-indicator {
                    margin-left: 5px;
                    opacity: 0.5;
                    font-size: 12px;
                }
                
                .sort-indicator.active {
                    opacity: 1;
                    color: #fff;
                }
                
                .table td {
                    font-size: 13px;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f3f4;
                }
                
                .table tbody tr:hover {
                    background-color: #f8f9fa;
                }
                
                .number-records {
                    color: #f44c00;
                    font-weight: bold;
                    font-size: 0.9em;
                }
                
                .bg-gred {
                    background: linear-gradient(135deg, #f44c00 0%, #ff6b35 100%) !important;
                }
                
                .bg-gred th {
                    color: white !important;
                    border-bottom-color: rgba(255,255,255,0.2) !important;
                }
                
                .staff-image {
                    transition: transform 0.2s ease;
                }
                
                .staff-image:hover {
                    transform: scale(1.1);
                }
                
                .btn-group-vertical .btn {
                    font-size: 11px;
                    padding: 0.25rem 0.5rem;
                }
                
                .btn-primary {
                    background-color: #007bff;
                    border-color: #007bff;
                }
                
                .btn-primary:hover {
                    background-color: #0056b3;
                    border-color: #0056b3;
                }
                
                .btn-danger {
                    background-color: #dc3545;
                    border-color: #dc3545;
                }
                
                .btn-danger:hover {
                    background-color: #c82333;
                    border-color: #bd2130;
                }
                
                .pagination .page-item.active .page-link {
                    background-color: #f44c00;
                    border-color: #f44c00;
                    color: white;
                }
                
                .pagination .page-link {
                    color: #f44c00;
                    border-color: #dee2e6;
                }
                
                .pagination .page-link:hover:not(.disabled) {
                    color: #d43f00;
                    background-color: #f8f9fa;
                    border-color: #f44c00;
                }
                
                .pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    cursor: not-allowed;
                }
                
                .badge {
                    font-size: 0.75em;
                }
                
                .text-truncate {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                .input-group-text {
                    background-color: #f8f9fa;
                    border-color: #ced4da;
                }
                
                .alert {
                    border: none;
                    border-radius: 0.5rem;
                }
                
                .spinner-border {
                    width: 2rem;
                    height: 2rem;
                }
                
                @media (max-width: 768px) {
                    .table-responsive {
                        font-size: 12px;
                    }
                    
                    .btn-group-vertical .btn {
                        font-size: 10px;
                        padding: 0.2rem 0.4rem;
                    }
                    
                    .staff-image, .image-placeholder {
                        width: 60px !important;
                        height: 60px !important;
                    }
                }
            `}</style>
        </>
    );
}